import { defaultM1Month, type KeywordVolumeRow } from "@/lib/keyword-volume-parser";

/**
 * Which calendar month M1 is, for every file of an upload - from the numbers.
 *
 * The same keyword x geo in two exports carries the same monthly values,
 * shifted by however many months lie between the exports: if the older file's
 * M1..M(12-s) equal the newer file's M(1+s)..M12, the newer M1 is s months
 * later. Measured 29.09.2026 over the 18 exports of 2026: from one confirmed
 * anchor (23.09 export = August) this placed 17 of 18 files, every one in
 * agreement with the export-date rule. The 18th shared no keyword with any
 * other file.
 *
 * Order of trust: months already stored (the server compares the file with
 * them), then other files of the same batch, then the export date.
 */

export type M1Method = "stored" | "file" | "date";

export interface M1Input {
  fileName: string;
  rows: KeywordVolumeRow[];
  exportedAt: Date;
  /** Server verdict against stored months, if it was confident. */
  stored?: { m1Month: string; matched: number; compared: number };
}

export interface M1Result {
  m1Month: string;
  method: M1Method;
  /** Human-readable evidence: "96% of 3,400 values match stored data". */
  detail: string;
  /** Set when some other pair of files disagrees - check this one. */
  conflict?: string;
}

// A shift counts only with this many identical rows, and twice as many as the
// runner-up: all-zero or flat rows match every shift and must not decide it.
const MIN_ROWS = 20;
const MAX_SHIFT = 5;

const toIndex = (iso: string) => {
  const [y, m] = iso.split("-").map(Number);
  return y * 12 + (m - 1);
};
const toIso = (i: number) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}-01`;

const informative = (m: (number | null)[]) => m.filter((v) => v != null && v > 0).length >= 3;

const sameRun = (a: (number | null)[], b: (number | null)[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** How many months newer file B is than file A, by identical shifted rows; null if unclear. */
export const monthsBetween = (a: KeywordVolumeRow[], b: KeywordVolumeRow[]): { shift: number; rows: number } | null => {
  const key = (r: KeywordVolumeRow) => `${r.keyword.toLowerCase()}|${r.geo}`;
  const bMap = new Map(b.filter((r) => informative(r.m)).map((r) => [key(r), r.m]));
  const counts = new Map<number, number>();
  for (const r of a) {
    if (!informative(r.m)) continue;
    const other = bMap.get(key(r));
    if (!other) continue;
    for (let s = -MAX_SHIFT; s <= MAX_SHIFT; s++) {
      const hit = s >= 0 ? sameRun(r.m.slice(0, 12 - s), other.slice(s)) : sameRun(other.slice(0, 12 + s), r.m.slice(-s));
      if (hit) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
  }
  const ranked = [...counts.entries()].sort((x, y) => y[1] - x[1]);
  const [best, second] = ranked;
  if (!best || best[1] < MIN_ROWS || best[1] < 2 * (second?.[1] ?? 0)) return null;
  return { shift: best[0], rows: best[1] };
};

export function detectM1Months(files: M1Input[]): M1Result[] {
  const out: (M1Result | undefined)[] = files.map((f) =>
    f.stored
      ? {
          m1Month: f.stored.m1Month,
          method: "stored",
          detail: `${Math.round((100 * f.stored.matched) / f.stored.compared)}% of ${f.stored.compared.toLocaleString("en-US")} values match stored data`,
        }
      : undefined,
  );

  const edges: { a: number; b: number; shift: number; rows: number }[] = [];
  for (let a = 0; a < files.length; a++) {
    for (let b = a + 1; b < files.length; b++) {
      const e = monthsBetween(files[a].rows, files[b].rows);
      if (e) edges.push({ a, b, ...e });
    }
  }

  // Strongest pair first: a month is carried along the edge with the most
  // identical rows, never along whichever weak edge happens to come first.
  const propagate = () => {
    for (;;) {
      let best: { from: number; to: number; shift: number; rows: number } | null = null;
      for (const e of edges) {
        const cand =
          out[e.a] && !out[e.b] ? { from: e.a, to: e.b, shift: e.shift, rows: e.rows }
          : out[e.b] && !out[e.a] ? { from: e.b, to: e.a, shift: -e.shift, rows: e.rows }
          : null;
        if (cand && (!best || cand.rows > best.rows)) best = cand;
      }
      if (!best) return;
      out[best.to] = {
        m1Month: toIso(toIndex(out[best.from]!.m1Month) + best.shift),
        method: "file",
        detail: `${best.rows.toLocaleString("en-US")} rows line up with ${files[best.from].fileName}`,
      };
    }
  };

  propagate();
  // Groups with no anchor: the newest file by its export date, the rest from it.
  for (;;) {
    const open = files.map((f, i) => ({ f, i })).filter(({ i }) => !out[i]);
    if (!open.length) break;
    const newest = open.reduce((x, y) => (y.f.exportedAt > x.f.exportedAt ? y : x));
    out[newest.i] = {
      m1Month: defaultM1Month(newest.f.exportedAt),
      method: "date",
      detail: `newest export here, M1 from its date ${newest.f.exportedAt.toLocaleDateString()} - the others are counted from it`,
    };
    propagate();
  }

  // Every other pair must agree with the months chosen; a file some pair
  // contradicts is marked, whatever carried its month.
  for (const e of edges) {
    const [ra, rb] = [out[e.a]!, out[e.b]!];
    if (toIndex(rb.m1Month) - toIndex(ra.m1Month) === e.shift) continue;
    for (const [r, other] of [[ra, files[e.b]], [rb, files[e.a]]] as const) {
      r.conflict = `${e.rows.toLocaleString("en-US")} rows with ${other.fileName} suggest a different month`;
    }
  }
  return out as M1Result[];
}
